"use client";

import "regenerator-runtime/runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";

const useSpeechToText = (onUpdateTranscript) => {
  const isNativeMode = !!window?.Android?.sttStart;
  const [nativeTranscript, setNativeTranscript] = useState("");
  const [nativeListening, setNativeListening] = useState(false);
  const timeoutRef = useRef(null);

  const {
    listening: browserListening,
    transcript: browserTranscript,
    resetTranscript: browserResetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const startNativeListening = () => {
    window.Android.sttStart();
    setNativeListening(true);
  };
  const stopNativeListening = () => {
    window.Android.sttStop();
    setNativeListening(false);
  };
  const resetNativeTranscript = () => setNativeTranscript("");

  const startListening = async () => {
    isNativeMode ? resetNativeTranscript() : browserResetTranscript();

    if (isNativeMode) {
      startNativeListening();
    } else if (browserSupportsSpeechRecognition) {
      SpeechRecognition.startListening({
        language: "ko-KR",
        continuous: true,
      });
    }
  };

  const stopListening = useCallback(() => {
    if (isNativeMode) {
      stopNativeListening();
    } else if (browserSupportsSpeechRecognition) {
      SpeechRecognition.stopListening();
    }
  }, [isNativeMode, browserSupportsSpeechRecognition]);

  const toggleListening = () => {
    const currentListening = isNativeMode ? nativeListening : browserListening;
    currentListening ? stopListening() : startListening();
  };

  useEffect(() => {
    window.onSttResult = (result) => {
      const { partial, full } = result ?? {};
      if (partial) setNativeTranscript(partial);
      if (full) setNativeTranscript(full);
    };

    return () => {
      window.onSttResult = undefined;
    };
  }, []);

  useEffect(() => {
    if (onUpdateTranscript && typeof onUpdateTranscript === "function") {
      onUpdateTranscript(isNativeMode ? nativeTranscript : browserTranscript);
    }
  }, [isNativeMode, nativeTranscript, browserTranscript]);

  useEffect(() => {
    const currentListening = isNativeMode ? nativeListening : browserListening;

    if (currentListening) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        stopListening();
        timeoutRef.current = null;
      }, 3500);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    isNativeMode,
    nativeListening,
    browserListening,
    nativeTranscript,
    browserTranscript,
    stopListening,
  ]);

  return {
    transcript: isNativeMode ? nativeTranscript : browserTranscript,
    listening: isNativeMode ? nativeListening : browserListening,
    startListening,
    stopListening,
    toggleListening,
    resetTranscript: isNativeMode
      ? resetNativeTranscript
      : browserResetTranscript,
    browserSupportsSpeechRecognition,
  };
};

export default useSpeechToText;
